import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {
  AUTH_COUNTRY_DEFAULT,
  AUTH_LOGIN_MAX_ATTEMPT,
  OTP_COOLDOWN,
  REGEX_LEADING_ZERO,
  REGEX_PHONE_NUMBER,
} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {
  AuthCountryResponse,
  AuthPhoneResendMethod,
  GiftshopEditProfileResendMethodType,
  GiftshopProfileVerifyPassword,
  PhoneForm,
  ProfileEditVerifMethod,
  ProfileEditVerifStatus,
} from 'types'
import {
  formatCooldownTime,
  formatPhoneNumber,
  showSnackbar,
  useDidMount,
  useHistory,
} from 'utils'
import {Button, Input, InputPhone} from 'common/components'
import {useDispatch, useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {
  VALIDATION_AUTH_LOGIN_PASSWORD,
  VALIDATION_AUTH_PHONE,
} from 'common/validations'
import {GiftShopTemplateOTPModal} from '../../../template'
import {
  GiftShopEditProfileLimitPasswordModal,
  GiftShopEditProfileModalVerifMethod,
  GiftShopEditProfileModalVerifStatus,
  GiftShopEditProfilePasswordVerifyModal,
  GiftShopEditProfilePhoneResendModal,
  GiftShopEditProfileSuccessStatus,
  GiftShopEditProfileVerifyOTPModal,
} from '../Modal'
import {GiftShopEditProfileSelfie} from '../Selfie'
import {GiftShopPhoneFormProps} from './GiftShopFormProps'

interface ScreenProps {
  isKyc: boolean
}

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: ${convertUnit(20)};
`

const StyledScreenContainer = styled.div<ScreenProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: ${({isKyc}) => (isKyc ? 0 : `${convertUnit(20)} auto`)};
  padding: ${({isKyc}) => (isKyc ? 0 : convertUnit(40))};
  border-radius: ${convertUnit(16)};
  background-color: ${({theme}) => theme.white_1};
  max-width: ${({isKyc}) => (isKyc ? `100%` : convertUnit(561))};
  height: ${({isKyc}) => (isKyc ? `100%` : `max-content`)};
`

export default function GiftshopPhoneForm({
  stateEditProfileScreen,
  stateKycScreen,
  stateActiveContent,
  statePhoneModal,
  stateIsFilled,
}: GiftShopPhoneFormProps) {
  const setActiveContent = stateActiveContent[1]
  const {update} = useDispatch()
  const {translate} = useTranslation()
  const history = useHistory()
  const {
    has_password,
    phone_code: oldPhoneCode,
    phone_number: oldPhoneNumber,
    email,
  } = useSelector('user') || {}
  const {isOldPhoneConfirmed, showModalPhoneOtp, showNewPhone} =
    useSelector('editProfileState') || {}
  const stateCountry = useState(AUTH_COUNTRY_DEFAULT)
  const [modalState, setModalState] = statePhoneModal
  const country = stateCountry[0]
  const successfulVerificationRef = useRef(true)
  const {verificationType} = useSelector('verificationStatus')
  const {phoneNumber, phoneCode, lastResendMethod} =
    useSelector('userNewPhone') || {}
  const {phoneOTPTime} = useSelector('lastUserState')
  const [verifStatus, setVerifStatus] = useState<
    ProfileEditVerifStatus | undefined
  >(undefined)

  const form = useForm<PhoneForm>({
    defaultValues: {
      phone:
        verificationType === 'add_phone' || showNewPhone ? phoneNumber : '',
      password: '',
    },
  })
  const {handleSubmit, watch, errors, setError, setValue} = form
  const {phone, password} = watch()

  const [loading, setLoading] = useState(false)
  const [publishedState, setPublishedState] = useState(false)
  const [cooldownTimer, setCooldownTimer] = useState('0')
  const resendMethodState = useState<GiftshopEditProfileResendMethodType>(
    lastResendMethod || 'whatsapp',
  )
  const resendMethod = resendMethodState[0]
  const [passwordVerifyModal, setPasswordVerifyModal] = useState<{
    visible: boolean
    item: GiftshopProfileVerifyPassword
  }>({
    visible: false,
    item: 'none',
  })
  const prompt = useMemo(() => (!!phone || !!password) && !publishedState, [
    password,
    phone,
    publishedState,
  ])
  const setIsFilled = stateIsFilled[1]
  const [shouldResend, setShouldResend] = useState(
    modalState === 'verify' || modalState === 'add-credential',
  )
  const [loadingInModal, setLoadingInModal] = useState(false)
  const screen = stateEditProfileScreen && stateEditProfileScreen[0]
  const setScreen = stateEditProfileScreen && stateEditProfileScreen[1]
  const stateKycActiveScreen = stateKycScreen
  const statePassword = useState('')
  const stateOtpEmailError = useState(false)
  const setOtpEmailError = stateOtpEmailError[1]
  const [otpEmailErrorCode, setOtpEmailErrorCode] = useState(200)
  const [resetWhenPress, setResetWhenPress] = useState(true)
  const isEditPhone = useMemo(() => !!(oldPhoneCode && oldPhoneNumber), [
    oldPhoneCode,
    oldPhoneNumber,
  ])

  const handleLoadDataPhoneDummy = useCallback(
    (search: string): AuthCountryResponse[] =>
      !search || 'indonesia'.includes(search.toLowerCase())
        ? [AUTH_COUNTRY_DEFAULT]
        : [],
    [],
  )

  const handlePasswordVerifyModal = useCallback(
    (item: GiftshopProfileVerifyPassword) =>
      setPasswordVerifyModal((prev) => ({visible: !prev.visible, item})),
    [],
  )

  const handleResetEditPhoneStatus = useCallback(() => {
    update('editProfileState', {
      isOldPhoneConfirmed: false,
    })
  }, [update])

  const resendPhoneOTP = useCallback(
    (method: GiftshopEditProfileResendMethodType) => {
      requestData('member_2fa_edit_phone_resend_new', {
        data: {verification_method: method === 'sms' ? 'phone' : 'whatsapp'},
        onRequestSuccess: ({status, data: {message, detail}}) => {
          if (status === 200) {
            update('userNewPhone', {lastResendMethod: method})
            update('lastUserState', {
              phoneOTPTime: new Date().getTime(),
            })
            setModalState('verify')
          } else if (status === 422 && message === 'no prior request') {
            handleResetEditPhoneStatus()
            setModalState('method')
          } else if (status === 429 && detail) {
            const {resend_in} = detail || {}
            const {hour, minute, second} = formatCooldownTime(
              resend_in,
              false,
              true,
            )
            showSnackbar(
              translate('auth:resendLimitReached', {
                duration: hour + minute + second,
              }),
            )
          } else {
            successfulVerificationRef.current = false
            setModalState('confirmation')
          }
        },
      })
    },
    [update, setModalState, handleResetEditPhoneStatus, translate],
  )

  const handleNavigateToKyc = useCallback(() => {
    setScreen && setScreen('kyc')
  }, [setScreen])

  const handleUpdateOtpTimePhone = useCallback(() => {
    update('lastUserState', {phoneOTPTime: new Date().getTime()})
    setResetWhenPress(true)
    setModalState(isEditPhone ? 'verify' : 'add-credential')
    setShouldResend(true)
  }, [isEditPhone, setModalState, update])

  const handleOnSendFirstOtpSuccess = useCallback(
    (method: ProfileEditVerifMethod) => {
      update('userNewPhone', {
        phoneCode: country.dial_code,
        phoneNumber: phone.replace(REGEX_LEADING_ZERO, ''),
        lastResendMethod: 'whatsapp',
      })
      update('editProfileState', {
        isOldPhoneConfirmed: false,
        verifMethodPhone: method,
        showNewPhone: true,
      })
      update('verificationStatus', {
        isPhoneVerified: false,
      })
      if (method === 'kyc') {
        statePassword[1](password)
        setModalState('hidden')
        handleNavigateToKyc()
      } else {
        handleUpdateOtpTimePhone()
      }
    },
    [
      country.dial_code,
      handleNavigateToKyc,
      handleUpdateOtpTimePhone,
      password,
      phone,
      setModalState,
      statePassword,
      update,
    ],
  )

  const handle2FAEditPhoneResendCurrent = useCallback(
    (method: ProfileEditVerifMethod) => {
      setLoadingInModal(true)
      requestData('member_2fa_edit_phone_resend_current', {
        data: {verification_method: method},
        onRequestReceived: () => {
          setLoadingInModal(false)
          setModalState('hidden')
        },
        onRequestSuccess: ({status, data: {message, detail}}) => {
          if (status === 200) {
            handleUpdateOtpTimePhone()
          } else if (status === 429 && detail) {
            if (message === 'too many request') {
              const {hour, minute, second} = formatCooldownTime(
                detail.resend_in,
                false,
                true,
              )
              showSnackbar(
                translate('auth:resendLimitReached', {
                  duration: hour + minute + second,
                }),
              )
            }
          } else {
            successfulVerificationRef.current = false
            setModalState('confirmation')
          }
        },
      })
    },
    [handleUpdateOtpTimePhone, setModalState, translate],
  )

  const handle2FAEditPhone = useCallback(
    (method?: ProfileEditVerifMethod) => {
      if (method) {
        setLoadingInModal(true)
      } else {
        setLoading(true)
      }
      requestData('member_2fa_edit_phone_send_current', {
        data: {
          password,
          phone_code: country.dial_code,
          phone_number: phone,
          verification_method: method,
        },
        onRequestReceived: () => {
          setLoadingInModal(false)
          setLoading(false)
          setModalState('hidden')
        },
        onRequestSuccess: ({status, data: {message, detail}}) => {
          if (status === 200) {
            setPublishedState(true)
            if (method) {
              handleOnSendFirstOtpSuccess(method)
            } else {
              setModalState('method')
            }
          } else if (
            status === 400 &&
            message === 'username and password mismatch' &&
            detail
          ) {
            setError('password', {
              type: 'manual',
              message: translate('auth:validationLoginAttempt', {
                attempt: AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.attempt, 10),
              }),
            })
          } else if (status === 409) {
            setError('phone', {
              type: 'manual',
              message: translate('auth:validationPhoneRegistered'),
            })
          } else if (status === 422 && message === 'password does not exist') {
            handlePasswordVerifyModal('phone')
          } else if (status === 423 && detail) {
            setCooldownTimer(detail.wait_time)
            setModalState('limit')
          } else if (status === 429 && detail) {
            const {hour, minute, second} = formatCooldownTime(
              detail.resend_in,
              false,
              true,
            )
            showSnackbar(
              translate('auth:resendLimitReached', {
                duration: hour + minute + second,
              }),
            )
          } else {
            successfulVerificationRef.current = false
            setModalState('confirmation')
          }
        },
      })
    },
    [
      country.dial_code,
      handleOnSendFirstOtpSuccess,
      handlePasswordVerifyModal,
      password,
      phone,
      setError,
      setModalState,
      translate,
    ],
  )

  const handleSubmitForm = useCallback(() => {
    const phoneChanged = phoneNumber ? phoneNumber !== phone : true
    if (phoneChanged) {
      setShouldResend(false)
      update('editProfileState', {verifMethodPhone: undefined})
      handle2FAEditPhone()
    } else if (
      new Date().getTime() / 1000 - phoneOTPTime / 1000 >
      OTP_COOLDOWN
    ) {
      if (isOldPhoneConfirmed) {
        setModalState('resend')
      } else {
        handle2FAEditPhone()
      }
    } else {
      setModalState(
        isEditPhone || isOldPhoneConfirmed ? 'verify' : 'add-credential',
      )
    }
  }, [
    handle2FAEditPhone,
    isEditPhone,
    isOldPhoneConfirmed,
    phone,
    phoneNumber,
    phoneOTPTime,
    setModalState,
    update,
  ])

  const handleRenderPasswordVerifyModal = useMemo(
    () => (
      <GiftShopEditProfilePasswordVerifyModal
        visible={passwordVerifyModal.visible}
        item={passwordVerifyModal.item}
        onCancel={() => {
          handlePasswordVerifyModal('none')
          history.goBack()
        }}
        onConfirm={() => setActiveContent('password')}
      />
    ),
    [
      handlePasswordVerifyModal,
      history,
      passwordVerifyModal.item,
      passwordVerifyModal.visible,
      setActiveContent,
    ],
  )

  const isButtonDisabled = useMemo(() => {
    const isValidPhone = errors.phone !== undefined || phone === ''
    const isValidPassword = errors.password !== undefined || password === ''

    return isValidPhone || isValidPassword
  }, [errors.password, errors.phone, password, phone])

  const handleRenderPhoneField = useMemo(
    () => (
      <InputPhone
        name="phone"
        loadData={handleLoadDataPhoneDummy}
        stateSelect={stateCountry}
        form={form}
        formRules={VALIDATION_AUTH_PHONE}
        allowedCharacters={REGEX_PHONE_NUMBER}
        emptyMessage={(search) =>
          translate('auth:registerIdentifierSearchCountryEmpty', {
            search,
          })
        }
        maxLength={13}
        label={translate('global:mobileNumberNew')}
        placeholder={translate('auth:registerIdentifierInputPhonePlaceholder')}
        searchPlaceholder={translate('global:search')}
        autoComplete={false}
        rightIcon="delete"
      />
    ),
    [form, handleLoadDataPhoneDummy, stateCountry, translate],
  )

  const handleRenderPasswordField = useMemo(
    () => (
      <Input
        type="password"
        name="password"
        form={form}
        formRules={VALIDATION_AUTH_LOGIN_PASSWORD}
        label={translate('global:password')}
        placeholder={translate('auth:loginInputPasswordPlaceholder')}
        rightIcon="visibility"
        containerStyle={{marginTop: convertUnit(20)}}
      />
    ),
    [form, translate],
  )

  const handleRenderButton = useMemo(
    () => (
      <StyledButton
        disabled={isButtonDisabled}
        isLoading={loading}
        label={translate('global:save')}
        onClick={handleSubmit(handleSubmitForm)}
      />
    ),
    [isButtonDisabled, loading, translate, handleSubmit, handleSubmitForm],
  )

  const handle2FAEditPhoneSendNew = useCallback(() => {
    setLoadingInModal(true)
    requestData('member_2fa_edit_phone_send_new', {
      data: {verification_method: 'whatsapp'},
      onRequestReceived: () => {
        setLoadingInModal(false)
        setModalState('hidden')
      },
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          update('lastUserState', {phoneOTPTime: new Date().getTime()})
          setVerifStatus('success')
          setModalState('verif-status')
        } else {
          successfulVerificationRef.current = false
          setModalState('confirmation')
        }
      },
    })
  }, [setModalState, update])

  const handle2FAAddPhoneVerifyCurrent = useCallback(
    (code: string) => {
      requestData('member_2fa_edit_phone_verify_current', {
        data: {code},
        onRequestSuccess: ({status, data: {detail}}) => {
          if (status === 200) {
            setPublishedState(true)
            setModalState('hidden')
            update('editProfileState', {
              isOldPhoneConfirmed: true,
              verifMethodPhone: undefined,
            })
            handle2FAEditPhoneSendNew()
          } else if (status === 400 && detail) {
            if (detail.code === 'code does not match') {
              setOtpEmailErrorCode(404)
              setOtpEmailError(true)
            }
          } else if (status === 410) {
            setOtpEmailErrorCode(410)
            setOtpEmailError(false)
          } else {
            setVerifStatus('failed')
            setModalState('verif-status')
          }
        },
      })
    },
    [handle2FAEditPhoneSendNew, setModalState, setOtpEmailError, update],
  )

  const handleRenderModal = useMemo(() => {
    switch (modalState) {
      case 'verify':
        return (
          <GiftShopEditProfileVerifyOTPModal
            verificationMethod={resendMethod}
            onCancel={() => setModalState('hidden')}
            onSuccess={() => {
              if (isEditPhone && !isOldPhoneConfirmed) {
                setModalState('hidden')
                update('editProfileState', {
                  isOldPhoneConfirmed: true,
                  verifMethodPhone: undefined,
                })
                handle2FAEditPhoneSendNew()
                return
              }
              successfulVerificationRef.current = true
              update('verificationStatus', {
                verificationType: 'none',
                isPhoneVerified: true,
              })
              update('user', {
                phone_code: phoneCode,
                phone_number: phoneNumber,
              })
              update('userNewPhone', {
                phoneCode: '',
                phoneNumber: '',
              })
              update('editProfileState', {
                isOldPhoneConfirmed: true,
                verifMethodPhone: undefined,
              })
              setModalState('confirmation')
            }}
            onResend={() =>
              setModalState(!isOldPhoneConfirmed ? 'method' : 'resend')
            }
            onFailed={() => {
              successfulVerificationRef.current = false
              setModalState('confirmation')
            }}
          />
        )
      case 'confirmation':
        return (
          <GiftShopEditProfileSuccessStatus
            success={successfulVerificationRef.current}
            desc={translate(
              successfulVerificationRef.current
                ? 'auth:verificationPhoneConfirmHeaderDescription'
                : 'giftShop:problemWhenProcessingRequest',
            )}
            onClickButton={() => {
              setModalState('hidden')
              if (successfulVerificationRef.current) {
                setActiveContent('profile')
              }
            }}
          />
        )
      case 'limit':
        return (
          <GiftShopEditProfileLimitPasswordModal
            cooldownTimer={cooldownTimer}
            onConfirm={() => setModalState('hidden')}
          />
        )
      case 'resend':
        return (
          <GiftShopEditProfilePhoneResendModal
            stateVerificationMethod={resendMethodState}
            onCancel={() => setModalState('hidden')}
            onSelect={(method) => resendPhoneOTP(method)}
            title={translate('giftShop:editPhoneNewNumberVerif')}
          />
        )
      case 'method':
        return (
          <GiftShopEditProfileModalVerifMethod
            method={isEditPhone ? 'phone' : 'email'}
            visible
            isAddCredential={!isEditPhone}
            toggleModal={() => setModalState('hidden')}
            loading={loadingInModal}
            oldCredential={
              isEditPhone
                ? `+${oldPhoneCode} ${formatPhoneNumber(oldPhoneNumber!)}`
                : email!
            }
            onSelect={(method) => {
              if (method === 'kyc') {
                handleOnSendFirstOtpSuccess(method)
              } else {
                if (shouldResend) {
                  handle2FAEditPhoneResendCurrent(
                    method as AuthPhoneResendMethod,
                  )
                  return
                }
                handle2FAEditPhone(method)
              }
            }}
          />
        )
      case 'verif-status':
        return (
          <GiftShopEditProfileModalVerifStatus
            method="phone"
            onPrimaryAction={() => {
              if (verifStatus === 'success') {
                setModalState('verify')
              } else {
                setModalState('hidden')
              }
            }}
            visible
            loading={loadingInModal}
            verifStatus={verifStatus}
          />
        )
      case 'add-credential':
        return (
          <GiftShopTemplateOTPModal
            stateError={stateOtpEmailError}
            timeRemaining={phoneOTPTime}
            verificationMethod="email"
            onResend={() => {
              setResetWhenPress(false)
              setModalState('method')
            }}
            onVerify={handle2FAAddPhoneVerifyCurrent}
            onCancel={() => setModalState('hidden')}
            dataToVerify={email!}
            bottomText={translate('global:email')}
            textAvailable={translate(
              'giftShop:editCredentialOtpResendAvailable',
            )}
            textCooldown="giftShop:editCredentialOtpResendCooldown"
            resetWhenPress={resetWhenPress}
            footerText={
              otpEmailErrorCode === 410
                ? translate('giftShop:codeExpired')
                : undefined
            }
          />
        )
      default:
        return null
    }
  }, [
    cooldownTimer,
    email,
    handle2FAAddPhoneVerifyCurrent,
    handle2FAEditPhone,
    handle2FAEditPhoneResendCurrent,
    handle2FAEditPhoneSendNew,
    handleOnSendFirstOtpSuccess,
    isEditPhone,
    isOldPhoneConfirmed,
    loadingInModal,
    modalState,
    oldPhoneCode,
    oldPhoneNumber,
    otpEmailErrorCode,
    phoneCode,
    phoneNumber,
    phoneOTPTime,
    resendMethod,
    resendMethodState,
    resendPhoneOTP,
    resetWhenPress,
    setActiveContent,
    setModalState,
    shouldResend,
    stateOtpEmailError,
    translate,
    update,
    verifStatus,
  ])

  const handleRenderScreen = useMemo(() => {
    switch (screen) {
      case 'kyc':
        return (
          stateKycActiveScreen && (
            <GiftShopEditProfileSelfie
              method="phone"
              stateScreen={stateKycActiveScreen}
              statePassword={statePassword}
              stateEditProfileScreen={stateEditProfileScreen}
              stateModal={statePhoneModal}
            />
          )
        )
      default:
        return (
          <>
            {handleRenderModal}
            {handleRenderPasswordVerifyModal}
            {handleRenderPhoneField}
            {handleRenderPasswordField}
            {handleRenderButton}
          </>
        )
    }
  }, [
    handleRenderButton,
    handleRenderModal,
    handleRenderPasswordField,
    handleRenderPasswordVerifyModal,
    handleRenderPhoneField,
    screen,
    stateEditProfileScreen,
    stateKycActiveScreen,
    statePassword,
    statePhoneModal,
  ])

  useEffect(() => {
    setIsFilled(prompt)
  }, [prompt, setIsFilled])

  useEffect(() => {
    screen === 'form' && showNewPhone && setValue('phone', phoneNumber)
  }, [phoneNumber, screen, setValue, showNewPhone])

  useDidMount(() => {
    if (!has_password) {
      handlePasswordVerifyModal('phone')
    }
  })

  useEffect(() => {
    if (showModalPhoneOtp) {
      update('editProfileState', {showModalPhoneOtp: false})
    }
  }, [showModalPhoneOtp, update])

  return (
    <StyledScreenContainer isKyc={screen === 'kyc'}>
      {handleRenderScreen}
    </StyledScreenContainer>
  )
}
